import React from 'react';
import {
    Modal,
    Table,
    Typography,
    Tag,
    Spin,
    Alert,
    Tooltip,
    Space,
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useAuth } from '../../../hooks/useAuth';
import { useGetJobHistoryQuery } from '../../../services/nestjs-base-api';

const { Text } = Typography;

interface JobHistoryEntry {
    id: number;
    jobName: string;
    instanceId: string;
    startedAt: string;
    completedAt?: string;
    status: string;
    errorMessage?: string;
    durationMs?: number;
}

interface JobHistoryModalProps {
    jobName: string | null;
    isOpen: boolean;
    onClose: () => void;
}

export const JobHistoryModal: React.FC<JobHistoryModalProps> = ({
    jobName,
    isOpen,
    onClose,
}) => {
    const { user } = useAuth();

    const {
        data: historyData = [],
        isLoading,
        error,
    } = useGetJobHistoryQuery(
        {
            jwtToken: user?.jwtToken || '',
            jobName: jobName || undefined,
            limit: 100,
        },
        {
            skip: !jobName || !isOpen,
        }
    );

    const formatDuration = (ms: number) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'SUCCESS':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'FAILED':
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'TIMEOUT':
                return <ClockCircleOutlined style={{ color: '#faad14' }} />;
            default:
                return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SUCCESS':
                return 'success';
            case 'FAILED':
                return 'error';
            case 'TIMEOUT':
                return 'warning';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            title: 'Started At',
            dataIndex: 'startedAt',
            key: 'startedAt',
            render: (date: string) => (
                <div>
                    <div>
                        <Text style={{ fontSize: '13px' }}>
                            {moment(date).format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                        {moment(date).fromNow()}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag
                    color={getStatusColor(status)}
                    icon={getStatusIcon(status)}
                >
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Duration',
            dataIndex: 'durationMs',
            key: 'durationMs',
            render: (duration: number | undefined, record: JobHistoryEntry) => {
                if (!duration) {
                    if (record.status === 'SUCCESS' || record.status === 'FAILED') {
                        return <Text type="secondary">-</Text>;
                    }
                    // Still running or incomplete
                    return (
                        <Text type="secondary">
                            {record.completedAt ? '-' : 'Running...'}
                        </Text>
                    );
                }
                return <Text>{formatDuration(duration)}</Text>;
            },
        },
        {
            title: 'Instance',
            dataIndex: 'instanceId',
            key: 'instanceId',
            render: (instanceId: string) => (
                <Text code style={{ fontSize: '11px' }}>
                    {instanceId}
                </Text>
            ),
        },
        {
            title: 'Completed At',
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: (date?: string) => {
                if (!date) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {moment(date).fromNow()}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Error',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            render: (error?: string) => {
                if (!error) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={error}>
                        <Text
                            type="danger"
                            ellipsis
                            style={{ maxWidth: 200, fontSize: '12px' }}
                        >
                            {error}
                        </Text>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <span>Job Execution History</span>
                    {jobName && (
                        <Text code style={{ fontSize: '12px' }}>
                            {jobName}
                        </Text>
                    )}
                </Space>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={1000}
            destroyOnClose
        >
            {isLoading && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            )}

            {error && (
                <Alert
                    message="Error loading job history"
                    description="Unable to fetch job execution history. Please try again later."
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {!isLoading && !error && historyData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">No execution history found for this job</Text>
                </div>
            )}

            {!isLoading && !error && historyData.length > 0 && (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary">
                            Showing last {historyData.length} executions
                        </Text>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={historyData}
                        rowKey="id"
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} executions`,
                        }}
                        scroll={{ x: 800 }}
                        size="small"
                    />
                </>
            )}
        </Modal>
    );
};